import { ShipmentStopType } from "@api/graphql/generated/generated-types";
import { DatePicker, Label } from "@src/common/components";
import { isoDateToDatePickerDate } from "@src/common/utils/timezoneConverters";
import React, { useEffect, useMemo } from "react";
import { Control, Controller, useFormContext } from "react-hook-form";

interface Props {
  name: string;
  control: Control;
  stopType: ShipmentStopType;
  selectedDuration: string;
  onStopDateChange?: () => void;
  index;
  formPathName?: string;
}

const StopDateSelector = ({
  name,
  control,
  stopType,
  selectedDuration,
  onStopDateChange,
  index,
  formPathName = "",
}: Props) => {
  const { setValue, watch, getValues } = useFormContext();

  const shipmentDate = watch(`${formPathName}shipmentDate`);
  const memoizedShipmentDate: Date = useMemo(
    () => shipmentDate,
    [shipmentDate]
  );
  let value = watch(name);

  // Convert ISO string to Date without timezone shift
  if (typeof value === "string") {
    value = isoDateToDatePickerDate(value);
  }

  useEffect(() => {
    const stopDate = getValues(`${formPathName}stops.${index}.stopDate`);
    if (selectedDuration === "hasStopDates" && memoizedShipmentDate) {
      if (stopDate) {
        return;
      }
      setStopDates();
    }
    if (selectedDuration === "noStopDates") {
      setValue(`${formPathName}stops.${index}.stopDate`, null);
      setValue(`${formPathName}stops.${index}.deliverByDate`, null);
    }
  }, [selectedDuration, memoizedShipmentDate, setValue, name, stopType]);

  const setStopDates = () => {
    if (stopType === ShipmentStopType.PickUp) {
      setValue(name, memoizedShipmentDate);
    } else {
      // Add 1 day using native Date to stay in browser timezone
      const nextDay = new Date(memoizedShipmentDate);
      nextDay.setDate(nextDay.getDate() + 1);

      const closeTime = getValues(
        `${formPathName}stops.${index}.timeWindow.close`
      );

      // Use closeTime if it's a Date, otherwise use nextDay
      const deliverByDate = closeTime instanceof Date ? closeTime : nextDay;
      setValue(`${formPathName}stops.${index}.deliverByDate`, deliverByDate);
      // Use shipmentDate directly - no moment conversion needed
      setValue(name, memoizedShipmentDate);
    }
  };

  return (
    <div className="flex flex-col">
      <Label className="truncate text-xs dark:text-gray-300">Route Date</Label>
      <Controller
        name={name}
        control={control}
        render={({ field: { onChange } }) => (
          <DatePicker
            size="mini"
            value={value}
            onChange={(value) => {
              if (value) {
                onChange(value.date);
                onStopDateChange && onStopDateChange();
              }
            }}
            minDate={memoizedShipmentDate}
            clearable={false}
          />
        )}
      />
    </div>
  );
};

export default StopDateSelector;
